import * as log from '../util/log';
import * as purchaseProcess from './transactionProcessPurchase';
import * as bookingProcess from './transactionProcessBooking';
import * as inquiryProcess from './transactionProcessInquiry';

// Supported unit types
// Note: These are passed to translations/microcopy in certain cases.
//       Therefore, they can't contain wordbreaks like '-' or space ' '
export const ITEM = 'item';
export const DAY = 'day';
export const NIGHT = 'night';
export const HOUR = 'hour';
export const INQUIRY = 'inquiry';

// Then names of supported processes
export const PURCHASE_PROCESS_NAME = 'default-purchase';
export const BOOKING_PROCESS_NAME = 'default-booking';
export const INQUIRY_PROCESS_NAME = 'default-inquiry';
export const MOBILE_BANKING_PROCESS = 'mobile-banking';
export const PROMPT_PAY_PROCESS = 'promptpay';

/**
 * A process should export:
 * - graph
 * - states
 * - transitions
 * - isRelevantPastTransition(transition)
 * - isPrivileged(transition)
 * - isCompleted(transition)
 * - isRefunded(transition)
 * - isCustomerReview(transition)
 * - isProviderReview(transition)
 * - statesNeedingCustomerAttention
 */
const PROCESSES = [
  {
    name: PURCHASE_PROCESS_NAME,
    alias: `${PURCHASE_PROCESS_NAME}/release-1`,
    process: purchaseProcess,
    unitTypes: [ITEM],
  },
  {
    name: BOOKING_PROCESS_NAME,
    alias: `${BOOKING_PROCESS_NAME}/release-1`,
    process: bookingProcess,
    unitTypes: [DAY, NIGHT, HOUR],
  },
  {
    name: INQUIRY_PROCESS_NAME,
    alias: `${INQUIRY_PROCESS_NAME}/release-1`,
    process: inquiryProcess,
    unitTypes: [INQUIRY],
  },
];

/**
 * Helper functions to figure out if transaction is in a specific state.
 * State is based on lastTransition given by transaction object and state description.
 *
 * @param {Object} tx transaction entity
 */
const txLastTransition = tx => tx?.attributes?.lastTransition;

/**
 * Get states from the graph.
 *
 * Note: currently we assume that state description is in stateX format
 *       and it doesn't contain nested states.
 *
 * @param {Object} graph Description of transaction process graph in StateX format
 */
const statesObjectFromGraph = graph => graph.states || {};

const getNextStateFromState = (process, stateName, transition) => {
  const statesObj = statesObjectFromGraph(process.graph);
  return stateName && transition ? statesObj[stateName]?.on?.[transition] || null : null;
};

const isDefaultBookingProcess = process => process?.graph?.id === bookingProcess.graph.id;

const normalizeProcessName = processName =>
  String(processName || '')
    .trim()
    .split('/')[0];

export const isLegacyPayLaterProcessName = processName =>
  [MOBILE_BANKING_PROCESS, PROMPT_PAY_PROCESS].includes(normalizeProcessName(processName));

export const getLegacyPaymentMethodFromProcessName = processName => {
  const baseProcessName = normalizeProcessName(processName);

  if (baseProcessName === MOBILE_BANKING_PROCESS) {
    return 'mobile_banking';
  }

  if (baseProcessName === PROMPT_PAY_PROCESS) {
    return 'promptpay';
  }

  return null;
};

const CARD_PAYMENT_METHODS = ['card-flow', 'card', 'defaultCard'];
const NON_CARD_PAYMENT_METHODS = ['promptpay', 'mobile_banking', 'mobile-banking'];

const normalizePaymentMethod = paymentMethod => {
  const normalized = String(paymentMethod || '')
    .trim()
    .toLowerCase();

  if (!normalized) {
    return null;
  }

  if (normalized === 'promptpay' || normalized === 'prompt_pay') {
    return 'promptpay';
  }

  if (
    normalized === 'mobile_banking' ||
    normalized === 'mobile-banking' ||
    normalized.startsWith('mobile_banking_')
  ) {
    return 'mobile_banking';
  }

  return CARD_PAYMENT_METHODS.includes(paymentMethod) ? paymentMethod : null;
};

export const getEffectivePaymentMethod = tx => {
  const metadata = tx?.attributes?.metadata || {};
  const protectedData = tx?.attributes?.protectedData || {};

  return (
    normalizePaymentMethod(metadata.paymentMethod) ||
    normalizePaymentMethod(protectedData.paymentMethod) ||
    normalizePaymentMethod(metadata.sourceType) ||
    normalizePaymentMethod(protectedData.sourceType) ||
    normalizePaymentMethod(metadata.selectedBank) ||
    normalizePaymentMethod(protectedData.selectedBank) ||
    (metadata.chargeSacner || metadata.qrCode || metadata.promptPayQr ? 'promptpay' : null) ||
    getLegacyPaymentMethodFromProcessName(tx?.attributes?.processName)
  );
};

const CUSTOM_OFFER_PAYMENT_REQUEST_TRANSITIONS = new Set([
  bookingProcess.transitions.REQUEST_CUSTOM_OFFER_PAYMENT_AFTER_INQUIRY,
  bookingProcess.transitions.REQUEST_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_INQUIRY,
  bookingProcess.transitions.APPLY_CUSTOM_OFFER_TO_BOOKING_REQUEST,
  bookingProcess.transitions.APPLY_CUSTOM_OFFER_PAYMENT_TO_BOOKING_REQUEST,
  bookingProcess.transitions.RETRY_CUSTOM_OFFER_PAYMENT_AFTER_FAILED,
  bookingProcess.transitions.RETRY_CUSTOM_OFFER_PAY_LATER_PAYMENT_AFTER_FAILED,
]);

export const isCardPaymentMethod = paymentMethod => CARD_PAYMENT_METHODS.includes(paymentMethod);

export const isNonCardPaymentMethod = paymentMethod =>
  NON_CARD_PAYMENT_METHODS.includes(paymentMethod);

export const isPayLaterPaymentMethod = paymentMethod =>
  isNonCardPaymentMethod(paymentMethod);

export const isPayLaterTransaction = tx => isPayLaterPaymentMethod(getEffectivePaymentMethod(tx));

export const isConsolidatedFlow = (tx, options = {}) => {
  if (options.isCustomOffer || options.isConsolidated) {
    return true;
  }

  const protectedData = tx?.attributes?.protectedData || {};
  const metadata = tx?.attributes?.metadata || {};
  const lastTransition = tx?.attributes?.lastTransition;

  return !!(
    protectedData.customOffer?.id ||
    protectedData.customOfferId ||
    protectedData.isCustomOfferBooking ||
    metadata.customOfferId ||
    metadata.customOfferPaymentContext ||
    CUSTOM_OFFER_PAYMENT_REQUEST_TRANSITIONS.has(lastTransition)
  );
};

export const isStandardFlow = (tx, options = {}) => !isConsolidatedFlow(tx, options);

const getProcessForTransaction = (tx, process) => {
  if (process) {
    return process;
  }

  const processName = tx?.attributes?.processName;
  if (!processName) {
    return null;
  }

  try {
    return getProcess(processName);
  } catch (e) {
    return null;
  }
};

const hasHostAcceptedForNonCardPayment = (tx, process) => {
  const resolvedProcess = getProcessForTransaction(tx, process);
  const txState = tx?.id && resolvedProcess ? resolvedProcess.getState(tx) : null;
  const lastTransition = tx?.attributes?.lastTransition;

  return (
    txState === resolvedProcess?.states?.ACCEPTED_PENDING_PAYMENT ||
    lastTransition === bookingProcess.transitions.ACCEPT_PAY_LATER ||
    lastTransition === bookingProcess.transitions.OPERATOR_ACCEPT_PAY_LATER ||
    (isPayLaterTransaction(tx) &&
      (lastTransition === bookingProcess.transitions.ACCEPT ||
        lastTransition === bookingProcess.transitions.OPERATOR_ACCEPT))
  );
};

export const canCreateNonCardPayment = (tx, options = {}) => {
  const paymentMethod = options.paymentMethod || getEffectivePaymentMethod(tx);

  if (!isNonCardPaymentMethod(paymentMethod)) {
    return false;
  }

  if (isConsolidatedFlow(tx, options)) {
    return true;
  }

  return hasHostAcceptedForNonCardPayment(tx, options.process);
};

const getPayLaterLegacyNextState = (process, tx, currentState, transition) => {
  if (!isDefaultBookingProcess(process)) {
    return null;
  }

  if (!isPayLaterTransaction(tx)) {
    return null;
  }

  const { states, transitions } = process;
  if (currentState === states.INITIAL && transition === transitions.REQUEST_PAYMENT) {
    return states.PENDING_PROVIDER_ACCEPTANCE;
  }

  if (currentState === states.INQUIRY && transition === transitions.REQUEST_PAYMENT_AFTER_INQUIRY) {
    return states.PENDING_PROVIDER_ACCEPTANCE;
  }

  if (
    currentState === states.PENDING_PROVIDER_ACCEPTANCE &&
    (transition === transitions.ACCEPT || transition === transitions.OPERATOR_ACCEPT)
  ) {
    return states.ACCEPTED_PENDING_PAYMENT;
  }

  if (
    currentState === states.PENDING_PROVIDER_ACCEPTANCE &&
    (transition === transitions.DECLINE || transition === transitions.OPERATOR_DECLINE)
  ) {
    return states.DECLINED;
  }

  if (
    currentState === states.PENDING_PROVIDER_ACCEPTANCE &&
    (transition === transitions.ACCEPTANCE_WINDOW_PASSED ||
      transition === transitions.OPERATOR_ACCEPTANCE_WINDOW_PASSED)
  ) {
    return states.EXPIRED;
  }

  if (
    currentState === states.ACCEPTED_PENDING_PAYMENT &&
    (transition === transitions.CONFIRM_PAYMENT ||
      transition === transitions.OPERATOR_CONFIRM_PAYMENT)
  ) {
    return states.ACCEPTED;
  }

  return null;
};

const sortTransitionsByCreatedAt = transitions =>
  [...transitions].sort((a, b) => {
    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : NaN;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : NaN;

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return aTime - bTime;
  });

const transitionHistoryWithLastTransition = tx => {
  const transitions = Array.isArray(tx?.attributes?.transitions) ? tx.attributes.transitions : [];
  const lastTransition = txLastTransition(tx);
  const lastHistoryEntry = transitions[transitions.length - 1];

  return lastTransition && lastHistoryEntry?.transition !== lastTransition
    ? [
        ...transitions,
        {
          transition: lastTransition,
          createdAt: tx?.attributes?.lastTransitionedAt,
        },
      ]
    : transitions;
};

const getStateFromTransitionHistory = process => tx => {
  const txTransitions = transitionHistoryWithLastTransition(tx);
  if (txTransitions.length === 0) {
    return null;
  }

  const resolved = sortTransitionsByCreatedAt(txTransitions).reduce(
    (acc, transitionEntry) => {
      const transition = transitionEntry?.transition;
      const legacyNextState = getPayLaterLegacyNextState(process, tx, acc.currentState, transition);
      const directNextState = getNextStateFromState(process, acc.currentState, transition);
      const nextState = legacyNextState || directNextState;

      return nextState ? { currentState: nextState, matchedTransition: true } : acc;
    },
    { currentState: process.graph.initial, matchedTransition: false }
  );

  return resolved.matchedTransition ? resolved.currentState : null;
};

const getLegacyPayLaterStateAfterLastTransition = process => tx => {
  if (!isDefaultBookingProcess(process)) {
    return null;
  }

  if (!isPayLaterTransaction(tx)) {
    return null;
  }

  const lastTransition = txLastTransition(tx);
  const { states, transitions } = process;
  switch (lastTransition) {
    case transitions.REQUEST_PAYMENT:
    case transitions.REQUEST_PAYMENT_AFTER_INQUIRY:
    case transitions.RETRY_PAYMENT_AFTER_FAILED:
      return states.PENDING_PROVIDER_ACCEPTANCE;
    case transitions.ACCEPT:
    case transitions.OPERATOR_ACCEPT:
    case transitions.ACCEPT_PAY_LATER:
    case transitions.OPERATOR_ACCEPT_PAY_LATER:
      return states.ACCEPTED_PENDING_PAYMENT;
    case transitions.CONFIRM_PAYMENT:
    case transitions.OPERATOR_CONFIRM_PAYMENT:
    case transitions.CONFIRM_PAY_LATER_PAYMENT:
    case transitions.OPERATOR_CONFIRM_PAY_LATER_PAYMENT:
      return states.ACCEPTED;
    default:
      return null;
  }
};

const getDefaultBookingFallbackState = (process, transition) => {
  if (!isDefaultBookingProcess(process)) {
    return null;
  }

  const { states, transitions } = process;
  switch (transition) {
    case transitions.ACCEPT:
    case transitions.OPERATOR_ACCEPT:
      return states.ACCEPTED;
    case transitions.ACCEPT_PAY_LATER:
    case transitions.OPERATOR_ACCEPT_PAY_LATER:
      return states.ACCEPTED_PENDING_PAYMENT;
    case transitions.CONFIRM_PAYMENT:
    case transitions.OPERATOR_CONFIRM_PAYMENT:
    case transitions.CONFIRM_CUSTOM_OFFER_PAYMENT:
    case transitions.OPERATOR_CONFIRM_CUSTOM_OFFER_PAYMENT:
      return states.PREAUTHORIZED;
    case transitions.CONFIRM_PAY_LATER_PAYMENT:
    case transitions.OPERATOR_CONFIRM_PAY_LATER_PAYMENT:
    case transitions.COMPLETE_CUSTOM_OFFER_PAYMENT:
    case transitions.CONFIRM_CUSTOM_OFFER_PAY_LATER_PAYMENT:
      return states.ACCEPTED;
    case transitions.SEND_CUSTOM_OFFER:
    case transitions.WITHDRAW_CUSTOM_OFFER:
    case transitions.DECLINE_CUSTOM_OFFER:
    case transitions.ACCEPT_CUSTOM_OFFER:
    case transitions.EXPIRE_CUSTOM_OFFER:
      return states.INQUIRY;
    default:
      return null;
  }
};

/**
 * This is a helper function that's attached to exported 'getProcess'.
 * Get next process state after given transition.
 *
 * @param {Object} process imported from a separate file
 * @returns {function} Returns a function to check the next state after given transition.
 */
const getStateAfterTransition = process => (transition, currentState, tx) => {
  if (currentState) {
    return (
      getPayLaterLegacyNextState(process, tx, currentState, transition) ||
      getNextStateFromState(process, currentState, transition)
    );
  }

  const defaultBookingFallbackState = getDefaultBookingFallbackState(process, transition);
  if (defaultBookingFallbackState) {
    return defaultBookingFallbackState;
  }

  const statesObj = statesObjectFromGraph(process.graph);
  const stateNames = Object.keys(statesObj);
  const fromState = stateNames.find(stateName => {
    const transitionsForward = Object.keys(statesObj[stateName]?.on || {});
    return transitionsForward.includes(transition);
  });

  return fromState && transition && statesObj[fromState]?.on[transition]
    ? statesObj[fromState]?.on[transition]
    : null;
};

/**
 * This is a helper function that's attached to exported 'getProcess' as 'getState'
 * Get state based on lastTransition of given transaction entity.
 *
 * How to use this function:
 *   // import { getProcess } from '../../transactions/transaction';
 *   const process = getProcess(processName);
 *   const state = process.getState(tx);
 *   const isInquiry = state === process.states.INQUIRY
 *
 * @param {Object} process imported from a separate file
 * @returns {function} Returns a function to check the current state of transaction entity against
 * given process.
 */
const getProcessState = process => tx => {
  return (
    getStateFromTransitionHistory(process)(tx) ||
    getLegacyPayLaterStateAfterLastTransition(process)(tx) ||
    getStateAfterTransition(process)(txLastTransition(tx))
  );
};

/**
 * Pick transition names that lead to target state from given entries.
 *
 * First parameter, "transitionEntries", should look like this:
 * [
 *   [transitionForward1, stateY],
 *   [transitionForward2, stateY],
 *   [transitionForward3, stateZ],
 * ]
 *
 * @param {Array} transitionEntries
 * @param {String} targetState
 * @param {Array} initialTransitions
 */
const pickTransitionsToTargetState = (transitionEntries, targetState, initialTransitions) => {
  return transitionEntries.reduce((pickedTransitions, transitionEntry) => {
    const [transition, nextState] = transitionEntry;
    return nextState === targetState ? [...pickedTransitions, transition] : pickedTransitions;
  }, initialTransitions);
};

/**
 * Get all the transitions that lead to specified state.
 *
 * Process uses following syntax to describe the graph:
 * states: {
 *   stateX: {
 *     on: {
 *       transitionForward1: stateY,
 *       transitionForward2: stateY,
 *       transitionForward3: stateZ,
 *     },
 *   },
 *   stateY: {},
 *   stateZ: {
 *     on: {
 *       transitionForward4: stateY,
 *     },
 *   },
 * },
 *
 * Finding all the transitions to 'stateY' should pick transitions: 1, 2, 4
 *
 * @param {Object} process
 * @param {String} targetState
 */
const getTransitionsToState = (process, targetState) => {
  const states = Object.values(statesObjectFromGraph(process.graph));

  return states.reduce((collectedTransitions, inspectedState) => {
    const transitionEntriesForward = Object.entries(inspectedState.on || {});
    return pickTransitionsToTargetState(
      transitionEntriesForward,
      targetState,
      collectedTransitions
    );
  }, []);
};

/**
 * Transitions that lead to given states.
 *
 * @param {Object} process against which transitions and states are checked.
 * @returns {function} Returns a function to get the transitions that lead to given states.
 */
const getTransitionsToStates = process => stateNames => {
  return stateNames.reduce((pickedTransitions, stateName) => {
    return [...pickedTransitions, ...getTransitionsToState(process, stateName)];
  }, []);
};

/**
 * Helper functions to figure out if transaction has passed a given state.
 * This is based on transitions history given by transaction object.
 *
 * @param {Object} process against which passed states are checked.
 */
const hasPassedState = process => (stateName, tx) => {
  const txTransitions = tx => transitionHistoryWithLastTransition(tx);
  const hasPassedTransition = (transitionName, tx) =>
    !!txTransitions(tx).find(t => t.transition === transitionName);

  const transitionHistory = txTransitions(tx);
  if (Array.isArray(transitionHistory) && transitionHistory.length > 0) {
    let currentState = process.graph.initial;

    const historyHasPassedState = sortTransitionsByCreatedAt(transitionHistory).some(
      transitionEntry => {
        const transition = transitionEntry?.transition;
        const legacyNextState = getPayLaterLegacyNextState(process, tx, currentState, transition);
        const directNextState = getNextStateFromState(process, currentState, transition);
        const nextState = legacyNextState || directNextState;

        if (nextState) {
          currentState = nextState;
        }

        return currentState === stateName;
      }
    );

    if (historyHasPassedState) {
      return true;
    }

    return getProcessState(process)(tx) === stateName;
  }

  return (
    getTransitionsToState(process, stateName).filter(t => hasPassedTransition(t, tx)).length > 0
  );
};

/**
 * If process has been renamed, but the graph itself is the same,
 * this function allows referencing the updated name of the process.
 * ProcessName is used in some translation keys and stateData functions.
 *
 * Note: If the process graph has changed, you must create a separate process graph for it.
 *
 * @param {String} processName
 */
export const resolveLatestProcessName = processName => {
  const processNameWithoutAlias = normalizeProcessName(processName);

  if (!processNameWithoutAlias) {
    return processName;
  }

  switch (processNameWithoutAlias) {
    case 'flex-product-default-process':
    case 'default-buying-products':
    case MOBILE_BANKING_PROCESS:
    case PROMPT_PAY_PROCESS:
      return BOOKING_PROCESS_NAME;
    case PURCHASE_PROCESS_NAME:
      return PURCHASE_PROCESS_NAME;
    case 'flex-default-process':
    case 'flex-hourly-default-process':
    case 'flex-booking-default-process':
    case BOOKING_PROCESS_NAME:
      return BOOKING_PROCESS_NAME;
    case INQUIRY_PROCESS_NAME:
      return INQUIRY_PROCESS_NAME;
    default:
      return processNameWithoutAlias;
  }
};

/**
 * Get process based on process name
 * @param {String} processName
 */
export const getProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  if (processInfo) {
    return {
      ...processInfo.process,
      getState: getProcessState(processInfo.process),
      getStateAfterTransition: getStateAfterTransition(processInfo.process),
      getTransitionsToStates: getTransitionsToStates(processInfo.process),
      hasPassedState: hasPassedState(processInfo.process),
    };
  } else {
    const error = new Error(`Unknown transaction process name: ${processName}`);
    log.error(error, 'unknown-transaction-process', { processName });
    throw error;
  }
};

/**
 * Get the info about supported processes: name, alias, unitTypes
 */
export const getSupportedProcessesInfo = () =>
  PROCESSES.map(p => {
    const { process, ...rest } = p;
    return rest;
  });

/**
 * Get all the transitions for every supported process
 */
export const getAllTransitionsForEveryProcess = () => {
  return PROCESSES.reduce((accTransitions, processInfo) => {
    return [...accTransitions, ...Object.values(processInfo.process.transitions)];
  }, []);
};

/**
 * Check if the process is purchase process
 *
 * @param {String} processName
 */
export const isPurchaseProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  return [PURCHASE_PROCESS_NAME].includes(processInfo?.name);
};

/**
 * Check if the process/alias points to a booking process
 *
 * @param {String} processAlias
 */
export const isPurchaseProcessAlias = processAlias => {
  const processName = processAlias ? processAlias.split('/')[0] : null;
  return processAlias ? isPurchaseProcess(processName) : false;
};

/**
 * Check if the process is booking process
 *
 * @param {String} processName
 */
export const isBookingProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  return [BOOKING_PROCESS_NAME].includes(processInfo?.name);
};

/**
 * Check if the process/alias points to a booking process
 *
 * @param {String} processAlias
 */
export const isBookingProcessAlias = processAlias => {
  const processName = processAlias ? processAlias.split('/')[0] : null;
  return processAlias ? isBookingProcess(processName) : false;
};

/**
 * Check from unit type if full days should be used.
 * E.g. unit type is day or night
 * This is mainly used for availability management.
 *
 * @param {String} unitType
 */
export const isFullDay = unitType => {
  return [DAY, NIGHT].includes(unitType);
};

/**
 * Get transitions that need provider's attention for every supported process
 */
export const getTransitionsNeedingProviderAttention = () => {
  return PROCESSES.reduce((accTransitions, processInfo) => {
    const statesNeedingProviderAttention = Object.values(
      processInfo.process.statesNeedingProviderAttention || []
    );
    const process = processInfo.process;
    const processTransitions = statesNeedingProviderAttention.reduce(
      (pickedTransitions, stateName) => {
        return [...pickedTransitions, ...getTransitionsToState(process, stateName)];
      },
      []
    );
    // Return only unique transitions names
    // TODO: this setup is subject to problems if one process has important transition named
    // similarly as unimportant transition in another process.
    return [...new Set([...accTransitions, ...processTransitions])];
  }, []);
};

/**
 * Get transitions that need customer's attention for every supported process
 */
export const getTransitionsNeedingCustomerAttention = () => {
  return PROCESSES.reduce((accTransitions, processInfo) => {
    const statesNeedingCustomerAttention = Object.values(
      processInfo.process.statesNeedingCustomerAttention || []
    );
    const process = processInfo.process;
    const processTransitions = statesNeedingCustomerAttention.reduce(
      (pickedTransitions, stateName) => {
        return [...pickedTransitions, ...getTransitionsToState(process, stateName)];
      },
      []
    );

    return [...new Set([...accTransitions, ...processTransitions])];
  }, []);
};

/**
 * Actors
 *
 * There are 4 different actors that might initiate transitions:
 */

// Roles of actors that perform transaction transitions
export const TX_TRANSITION_ACTOR_CUSTOMER = 'customer';
export const TX_TRANSITION_ACTOR_PROVIDER = 'provider';
export const TX_TRANSITION_ACTOR_SYSTEM = 'system';
export const TX_TRANSITION_ACTOR_OPERATOR = 'operator';

export const TX_TRANSITION_ACTORS = [
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  TX_TRANSITION_ACTOR_SYSTEM,
  TX_TRANSITION_ACTOR_OPERATOR,
];

/**
 * Get the role of the current user on given transaction entity.
 *
 * @param {UUID} currentUserId UUID of the currentUser entity
 * @param {Object} transaction Transaction entity from Marketplace API
 */
export const getUserTxRole = (currentUserId, transaction) => {
  const customer = transaction?.customer;
  if (currentUserId && currentUserId.uuid && transaction?.id && customer.id) {
    // user can be either customer or provider
    return currentUserId.uuid === customer.id.uuid
      ? TX_TRANSITION_ACTOR_CUSTOMER
      : TX_TRANSITION_ACTOR_PROVIDER;
  } else {
    throw new Error(`Parameters for "userIsCustomer" function were wrong.
      currentUserId: ${currentUserId}, transaction: ${transaction}`);
  }
};

/**
 * Wildcard string for ConditionalResolver's conditions.
 */
export const CONDITIONAL_RESOLVER_WILDCARD = '*';

/**
 * This class helps to resolve correct UI data for each combination of conditional data [state & role]
 *
 * Usage:
 *  const stateData = new ConditionalResolver([currentState, currentRole])
 *    .cond(['inquiry', 'customer'], () => {
 *      return { showInfoX: true, isSomethingOn: true };
 *    })
 *    .cond(['purchase', _], () => {
 *      return { showInfoX: false, isSomethingOn: true };
 *    })
 *    .default(() => {
 *      return { showDetailCardHeadings: true };
 *    })
 *    .resolve();
 */
export class ConditionalResolver {
  constructor(data) {
    this.data = data;
    this.resolver = null;
    this.defaultResolver = null;
  }
  cond(conditions, resolver) {
    if (conditions?.length === this.data.length && this.resolver == null) {
      const isDefined = item => typeof item !== 'undefined';
      const isWildcard = item => item === CONDITIONAL_RESOLVER_WILDCARD;
      const isMatch = conditions.reduce(
        (isPartialMatch, item, i) =>
          isPartialMatch && isDefined(item) && (isWildcard(item) || item === this.data[i]),
        true
      );
      this.resolver = isMatch ? resolver : null;
    }
    return this;
  }
  default(defaultResolver) {
    this.defaultResolver = defaultResolver;
    return this;
  }
  resolve() {
    // This resolves the output against current conditions.
    // Therefore, call for resolve() must be the last call in method chain.
    return this.resolver ? this.resolver() : this.defaultResolver ? this.defaultResolver() : null;
  }
}
